import AppCommandBar from 'app/AppCommandBar'
import { FlexSpinner } from 'components'
import { ViewAuthorizer } from 'components/AccessDenied'
import { StickyPane } from 'components/StickyPane'
import { ConfirmDeleteDialog } from 'dialogs/ConfirmDeleteDialog'
import { FormikActions } from 'formik'
import {
  ConvoluteRuleForm,
  ConvoluteRuleFormValues,
} from 'forms/ConvoluteRuleForm'
import {
  CreateRuleForm,
  CreateRuleFormSchema,
  CreateRuleFormValues,
} from 'forms/CreateRuleForm'
import { EvaluatePersonForm } from 'forms/EvaluatePersonForm'
import { omitDeep } from 'helpers'
import { useCommand, useCreateCommand, useEditCommand } from 'hooks/useCommand'
import { useDialogState } from 'hooks/useDialogState'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import { usePanelState } from 'hooks/usePanelState'
import { useSelection } from 'hooks/useSelection'
import { Convolute, GdprRule, GdprRuleSetType, UserRole } from 'model'
import { Panel, PanelType } from 'office-ui-fabric-react'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { FormType } from 'views/helpers'
import { Section } from 'views/ProvingDetailsView/Layout'
import {
  useConvolutesQuery,
  useCreateGdprRuleMutation,
  useCreateOrUpdateConvoluteMutation,
  useDeleteGdprRuleMutation,
  useReadGdprRulesQuery,
  useUpdateGdprRuleMutation,
} from './AdminDsgvoView.generated'
import { DsgvoList } from './DsgvoList'
import { RuleSetList } from './RuleSetList'

const AdminDsgvoListView: React.FC = () => {
  const { t } = useTranslation()
  const [panelState, showPanel, hidePanel] = useFormikPanelState()
  const [rulePanelState, showRulePanel, hideRulePanel] = useFormikPanelState()
  const [evaluatePersonPanelState, showEvaluatePersonPanel] = usePanelState()
  const [dialogState, , hideDialog] = useDialogState()
  const [formType, setFormType] = React.useState()
  const [selectedItemId, setSelectedItemId] = React.useState()
  const [, setSelectedRuleId] = React.useState()
  const [createOrUpdateConvolute] = useCreateOrUpdateConvoluteMutation()
  const [createGdprRule] = useCreateGdprRuleMutation()
  const [updateGdprRule] = useUpdateGdprRuleMutation()
  const [deleteGdprRule] = useDeleteGdprRuleMutation()
  const { loading, error, refetch, data } = useReadGdprRulesQuery()
  const { data: convolutes, refetch: refetchConvolutes } = useConvolutesQuery()
  const items = data && data.gdprRules ? data.gdprRules : []
  const convolutesData =
    convolutes && convolutes.convolutes ? convolutes.convolutes : []
  const [ruleSelection, selectedRuleItems] = useSelection<Convolute>({
    getKey: (convolute) => convolute._id!,
  })
  const [selection, selectedItems] = useSelection<GdprRule>({
    getKey: (item) => item._id!,
  })

  const handleRulesSubmit = async (
    values: any,
    formikActions: FormikActions<ConvoluteRuleFormValues>
  ) => {
    formikActions.setSubmitting(true)

    await createOrUpdateConvolute({
      variables: {
        input: values,
      },
    }).then((_) => refetchConvolutes())
    formikActions.setSubmitting(false)
    hideRulePanel()
  }

  const handleSubmit = async (
    values: CreateRuleFormValues,
    formikActions: FormikActions<CreateRuleFormValues>
  ) => {
    formikActions.setSubmitting(true)
    if (formType === FormType.insert) {
      await createGdprRule({
        variables: { input: values },
      })
    } else if (formType === FormType.update) {
      await updateGdprRule({
        variables: { id: selectedItemId, update: values },
      })
    } else {
      throw new Error(
        'Custom: Error while creating or updating GdprRule. FormType not set.'
      )
    }
    formikActions.setSubmitting(false)
    hidePanel()
    refetch()
  }

  const handleConfirmDelete = async () => {
    if (selectedItems.length > 0) {
      const ids = selectedItems.map((x) => x._id)
      await deleteGdprRule({
        variables: { ids },
      })
    }
    refetch()
    hideDialog()
  }

  /** commands */
  useCommand(
    {
      key: 'setRule',
      text: 'Regeln zuweisen',
      disabled: selectedRuleItems.length === 0,
      priority: 10,
      iconProps: { iconName: 'Link' },
      onClick: () => {
        setSelectedRuleId(selectedRuleItems[0]._id)
        showRulePanel({
          type: selectedRuleItems[0].type,
          activeRuleset: selectedRuleItems[0].activeRuleset,
        })
      },
    },
    [selectedRuleItems]
  )

  useCommand({
    key: 'evaluation',
    text: 'Auswerten',
    priority: 50,
    iconProps: { iconName: 'ProfileSearch' },
    onClick: () => {
      showEvaluatePersonPanel()
    },
  })

  useCreateCommand(
    'rule',
    {
      priority: 20,
      onClick: () => {
        setFormType(FormType.insert)
        showPanel({ deletionPeriod: 0, retentionPeriod: 0 })
      },
    },
    [setFormType]
  )

  useEditCommand(
    { name: 'rule', count: selectedItems.length },
    {
      priority: 30,
      onClick: () => {
        setFormType(FormType.update)
        setSelectedItemId(selectedItems[0]._id)
        showPanel(
          omitDeep(selectedItems[0], [
            'id',
            '__typename',
            'created',
            'createdBy',
            'modified',
            'modifiedBy',
          ]) as GdprRuleSetType
        )
      },
    },
    [selectedItems, setFormType]
  )

  // useDeleteCommand(
  //   { name: 'rule', count: selectedItems.length },
  //   {
  //     priority: 40,
  //     onClick: showDialog,
  //   },
  //   [selectedItems]
  // )

  return (
    <FlexSpinner loading={loading} error={error}>
      <AppCommandBar />
      <Section label="Konvolute">
        <RuleSetList items={convolutesData as any} selection={ruleSelection} />
      </Section>
      <Section label="Regelliste">
        <DsgvoList items={items} selection={selection} />
      </Section>
      <FormikPanel
        headerText={
          formType === FormType.update
            ? t('model.rule.edit')
            : t('model.rule.create')
        }
        {...panelState}
        onSubmit={handleSubmit}
        validationSchema={new CreateRuleFormSchema()}
      >
        <CreateRuleForm />
      </FormikPanel>
      <FormikPanel
        {...rulePanelState}
        headerText={t('model.rule.set')}
        onSubmit={handleRulesSubmit}
      >
        <ConvoluteRuleForm />
      </FormikPanel>
      <Panel
        type={PanelType.medium}
        headerText="Auswerten"
        {...evaluatePersonPanelState}
      >
        <EvaluatePersonForm />
      </Panel>
      <ConfirmDeleteDialog
        {...dialogState}
        onConfirm={handleConfirmDelete}
        itemCount={selectedItems.length}
      />
    </FlexSpinner>
  )
}

export const AdminDsgvoView: React.FC = () => (
  <ViewAuthorizer roles={[UserRole.SystemAdministrator]}>
    <StickyPane>
      <AdminDsgvoListView />
    </StickyPane>
  </ViewAuthorizer>
)
